import { CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { ASSETS } from "../../../../utils/config";
import { goToUrl } from "../../../../utils/helpers";
import TextView from "../../../TextView";
import { fileIcons } from "../../../../constants/documentIcon";
import { PDF_PREVIEW_STYLES_ENABLED } from "../../../../constants/featureFlags";
import { resolvePreviewStyle, isPreviewCardStyle } from "./documentPreview";
import DocumentPreviewCard from "./DocumentPreviewCard";
import { isPdfUrl, PdfViewerModal } from "../../../InAppDocViewer";

const Container = styled.div`
  padding: ${(props) => props.padding};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const IconView = styled.div`
  width: 34px;
  height: 40px;
  display: flex;
  justify-content: flex-start;
`;

const Icon = styled.img`
  width: 34px;
  height: 40px;
  object-fit: fill;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const DocumentPreview = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;


const DocumentBlock = ({ doc, showThumbnail, previewStyle }) => {
  const location = useLocation();
  const isEditPage = location.pathname.includes("builder");
  // Defensive: a broken/missing file-type icon falls back to the generic doc
  // icon rather than rendering a broken <img>. Additive only.
  const [iconError, setIconError] = useState(false);
  // EW-608: in-app PDF viewer url for the DEFAULT icon-list render. Null = closed.
  // Mirrors DocumentItem / WheelRightSideBar(renderMedia,hotbar) / DocumentPreviewCard
  // so EVERY document surface behaves the same — a PDF opens in the in-app viewer
  // (canvas page-render + zoom) instead of the legacy external tab. The prior
  // ISP-4017 sweep fixed those siblings but MISSED this block; the icon-list path
  // still hard-coded goToUrl (external), so PDFs never rendered in-app here.
  const [inAppPdfUrl, setInAppPdfUrl] = useState(null);
  // Resolve the icon for this file type; default to the generic doc icon when
  // the type is unmapped (e.g. "txt", "zip") so the src is never undefined.
  const iconSrc = (!iconError && fileIcons[doc.type]) || ASSETS.doc;
  if (doc.loading)
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </div>
    );

  // DL-049 (flag-gated): when PDF_PREVIEW_STYLES is ON and the block chose a
  // preview CARD style ("full" | "cover"), render the elevated card. When the
  // flag is OFF, resolvePreviewStyle ALWAYS returns "icon", so control falls
  // through to the byte-identical icon+filename row below.
  const effectiveStyle = resolvePreviewStyle(
    PDF_PREVIEW_STYLES_ENABLED,
    previewStyle
  );
  if (isPreviewCardStyle(effectiveStyle)) {
    return (
      <DocumentPreviewCard
        doc={doc}
        previewStyle={effectiveStyle}
        isEditPage={isEditPage}
      />
    );
  }

  // EW-608 fix: on the VIEW surface (isEditPage=false), a PDF opens in the in-app
  // viewer modal (renders the PDF in-app) instead of navigating to an external
  // tab; non-PDF documents keep the legacy open-externally behaviour. The builder
  // (edit) page is unchanged — a click still does nothing there, so it never
  // interferes with selecting/editing the block. Any pdfjs failure degrades in the
  // modal to an "Open in a new tab" button, so this is never worse than today.
  const handleClick = () => {
    if (isEditPage) return;
    if (isPdfUrl(doc.src)) {
      setInAppPdfUrl(doc.src);
    } else {
      goToUrl(doc.src);
    }
  };

  return (
    <>
      <DocumentPreview showThumbnail={showThumbnail} onClick={handleClick}>
        <Row>
          <IconView>
            <Icon
              src={iconSrc}
              alt={doc.title || "Document"}
              onError={() => setIconError(true)}
            />
          </IconView>
          <TextView variant="largeText">{doc.title}</TextView>
        </Row>
      </DocumentPreview>
      <PdfViewerModal
        open={Boolean(inAppPdfUrl)}
        url={inAppPdfUrl}
        onClose={() => setInAppPdfUrl(null)}
      />
    </>
  );
};
export default DocumentBlock;
